import React, { useState, useEffect } from 'react';
import Tool, { type ToolHandler } from '../Tool';
import type { AssistantToolCall } from 'components/framework/models';
import { AhaButton } from 'components/common/reactifyWebComponent';
import type Agent from '../../agents/Agent';
import styles from './DocumentEditToolHandler.module.css';

const STORAGE_KEY = 'aha.assistant.documentEdit.showDiff';

/**
 * Toggle button for showing/hiding deletions in diff view
 */
const DiffToggleButton: React.FC<{ toolCallId: string }> = ({ toolCallId }) => {
  // Initialize from localStorage, default to true if not found
  const [showDiff, setShowDiff] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    const toolCallElement = document.querySelector(
      `[data-tool-call-id="${toolCallId}"]`
    );
    const diffElement = toolCallElement?.querySelector(
      ':scope .document-edit-diff'
    );

    if (diffElement) {
      if (showDiff) {
        diffElement.classList.add('diff-view');
      } else {
        diffElement.classList.remove('diff-view');
      }
    }
  }, [showDiff, toolCallId]); // Run only on mount

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(showDiff));
  }, [showDiff]);

  const toggleDiffView = (e: React.MouseEvent) => {
    e.stopPropagation();

    const toolCallElement = document.querySelector(
      `[data-tool-call-id="${toolCallId}"]`
    );
    const diffElement = toolCallElement?.querySelector(
      ':scope .document-edit-diff'
    );

    if (diffElement) {
      if (showDiff) {
        diffElement.classList.remove('diff-view');
      } else {
        diffElement.classList.add('diff-view');
      }
      setShowDiff(!showDiff);
    }
  };

  return (
    <>
      <AhaButton
        kind='icon'
        size='mini'
        onClick={toggleDiffView}
        active={showDiff}
      >
        <svg
          className={styles.toggleIcon}
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M11.3877 2.54661C12.1822 1.40469 13.7532 1.1241 14.8936 1.92064L15.0977 2.07884C16.0612 2.9098 16.2606 4.35251 15.5186 5.41966L11.7549 10.8308C11.3529 11.4087 11.1498 11.6977 10.9229 11.9597L10.7471 12.155C10.3268 12.6024 9.84528 12.9892 9.31641 13.3025L9.08301 13.4314C8.83714 13.5607 8.53674 13.6993 8.05664 13.9187L7.59668 14.1277C6.92565 14.4342 6.58948 14.588 6.33496 14.5144L6.22168 14.4704C6.11464 14.4182 6.02158 14.3393 5.95215 14.2409C5.83769 14.0782 5.82879 13.8296 5.86328 13.406L5.91016 12.9206L5.96484 12.3699C5.98777 12.1424 6.00749 11.9519 6.02539 11.7878L7.06348 11.5222C7.03387 11.7351 7.00587 12.014 6.95996 12.4695L6.9043 13.0212C6.89121 13.1511 6.88088 13.2622 6.87207 13.3572C6.95945 13.3181 7.06225 13.2721 7.18164 13.2175L7.64063 13.0085C8.29936 12.7076 8.56364 12.5854 8.80566 12.4421C9.31766 12.1389 9.77831 11.7542 10.168 11.3044C10.352 11.0919 10.5202 10.8538 10.9336 10.2595L14.0732 5.7429L11.5898 4.00657L10.46 5.6306L9.79688 4.83372L11.3877 2.54661ZM0.634766 5.61888C2.73252 2.77906 6.98001 2.77954 9.07813 5.61888C9.11659 5.67096 9.16519 5.74027 9.25 5.86107C9.26633 5.88432 9.27865 5.90142 9.28906 5.91673C9.82216 6.70237 9.81293 7.73658 9.26563 8.51243C9.25482 8.52774 9.24211 8.5441 9.22461 8.5681C9.114 8.71978 9.05177 8.8057 9.00195 8.86986C6.90074 11.5756 2.81222 11.5756 0.710938 8.86986C0.661234 8.80581 0.598401 8.71911 0.488282 8.5681C0.471197 8.54467 0.458872 8.52749 0.448243 8.51243C-0.0989256 7.73648 -0.108505 6.70231 0.424805 5.91673C0.435333 5.90123 0.447271 5.88471 0.463868 5.86107C0.548921 5.73992 0.596244 5.67103 0.634766 5.61888ZM8.27441 6.21361C6.57608 3.91515 3.13755 3.91483 1.43945 6.21361C1.41003 6.25346 1.37083 6.30908 1.28223 6.43529C1.26369 6.46169 1.25704 6.47077 1.25195 6.47825C0.952528 6.91931 0.958523 7.50056 1.26563 7.93626C1.27074 7.94349 1.27808 7.95346 1.29688 7.97923C1.41186 8.13692 1.46297 8.20759 1.50098 8.25657C3.20194 10.4467 6.51206 10.4469 8.21289 8.25657C8.25096 8.20754 8.30158 8.1375 8.41699 7.97923C8.43639 7.95264 8.44307 7.94359 8.44824 7.93626C8.75558 7.50064 8.76116 6.91942 8.46191 6.47825C8.45688 6.47087 8.44966 6.46095 8.43164 6.43529C8.34351 6.30975 8.30378 6.25339 8.27441 6.21361ZM4.85449 5.62669C5.70794 5.62688 6.39625 6.31622 6.39648 7.16966C6.39629 8.02427 5.7032 8.71146 4.85449 8.71165C4.00126 8.71118 3.31172 8.02296 3.31152 7.16966C3.31176 6.3164 4.00128 5.62716 4.85449 5.62669ZM4.85449 6.62669C4.55357 6.62716 4.31176 6.86868 4.31152 7.16966C4.31171 7.47068 4.55355 7.71119 4.85449 7.71165C5.15319 7.71146 5.39629 7.46972 5.39648 7.16966C5.39625 6.86851 5.15565 6.62688 4.85449 6.62669ZM14.3213 2.74095C13.6343 2.26098 12.6877 2.43006 12.209 3.1179L12.1602 3.18529L14.6455 4.92259L14.6973 4.8474C15.1736 4.16172 15.0052 3.21976 14.3213 2.74095Z'
            fill='currentColor'
          />
        </svg>
      </AhaButton>
      <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
        {showDiff ? 'Hide diff' : 'Show diff'}
      </aha-tooltip>
    </>
  );
};

/**
 * Handler for WritingAgent::DocumentEditTool
 * Handles document editing operations in the assisted editor
 */
function applyDocumentEditToolCall(
  toolCall: AssistantToolCall,
  agent: Agent
): {
  replaced: boolean;
  currentContent?: string;
} {
  let operationType: any = 'replace_text';
  let fromText = '';
  let toText = '';

  if (toolCall.content?.data) {
    const data = JSON.parse(toolCall.content?.data) as any;
    // TODO: remove data.where once we have migrated all tool calls
    if (data && data.where) {
      operationType = data.where;
    }
    if (data && data.operation_type) {
      operationType = data.operation_type;
    }
    if (data && data.old_text) {
      fromText = data.old_text;
    }
    if (data && data.new_text) {
      toText = data.new_text;
    }
  }

  if (!agent.contexts.assistedEditor) {
    console.warn(
      'WritingAgent::applyDocumentEditToolCall - No assisted editor context available'
    );
    return { replaced: false };
  }

  // Route to appropriate method based on content format
  const editorHtml = agent.contexts.assistedEditor.getEditorHtml();

  if (
    agent.contexts.assistedEditor.updateEditorHtml(
      editorHtml,
      toText,
      fromText,
      operationType
    )
  ) {
    return { replaced: true };
  } else {
    console.warn(
      'WritingAgent::applyDocumentEditToolCall - Failed to update editor with HTML content'
    );
    return { replaced: false, currentContent: editorHtml };
  }
}

const documentEditToolHandler: ToolHandler = {
  beforeApproveToolCall(
    toolCall: AssistantToolCall,
    agent: Agent,
    data?: Record<string, unknown> | string
  ): Record<string, unknown> | undefined {
    return applyDocumentEditToolCall(toolCall, agent);
  },

  unavailable(agent: Agent): boolean {
    return !agent.contexts.assistedEditor;
  },

  getPreviewHeaderActions(
    toolCall: AssistantToolCall,
    agent: Agent
  ): React.ReactNode | null {
    // Only show toggle for tool calls with preview content
    if (!toolCall.content?.preview?.body) {
      return null;
    }

    if (
      ['processing', 'needs_approval', 'complete', 'rejected'].includes(
        toolCall.status
      )
    ) {
      return <DiffToggleButton toolCallId={toolCall.id} />;
    }
  },
};

Tool.register('WritingAgent::DocumentEditTool', documentEditToolHandler);
